import { FormEvent, useEffect, useState } from 'react';
import { IClaimDeclineResponseItem, IWatrRequest } from '@nkr/models/index';
import { FormControl, Button, ControlLabel, Modal, FormGroup, Well, Alert, Checkbox } from 'react-bootstrap';
import usePrevious from '@nkr/services/use-previous';
import ReactHtmlParser from 'react-html-parser'
import { ClaimType, DashDonationType } from '@nkr/enums/index';
import { buildDeclineSelectionPayload, IDeclineSelectionPayload } from '../banking-decline-flags';
import { getBankingReviewTerminology } from '../banking-review-terminology';

/** Inline styles for donor email HTML (matches common client defaults; avoids visited/unvisited color split). */
function declineEmailAnchor(href: string, innerHtml: string): string {
    return `<a href="${href}" style="color: #337ab7; text-decoration: underline;">${innerHtml}</a>`;
}

interface IProps {
    show: boolean,
    data: IWatrRequest,
    declineType: string,
    onClose: () => void,
    onDeclineRequest: (requestId: number, emailSubject: string, firstParagraph: string, emailContent: string, lastParagraph: string, isFixable: boolean, bccCenter: boolean, selection: IDeclineSelectionPayload) => void,
    hasLineItemsSelected?: boolean,
    allowWageSelection?: boolean,
    claimDeclineResponseList: IClaimDeclineResponseItem[]
}

function buildUnifiedFirstParagraph(
    data: IWatrRequest,
    wages: boolean,
    routing: boolean,
    account: boolean,
    policyLink: string,
    dashboardLink: string
): string {
    const name = data.firstName;
    const bankingTerms = getBankingReviewTerminology(data.country, data.isUsBank);
    const hereLink = declineEmailAnchor(dashboardLink, 'here');
    const items: string[] = [];
    if (wages) {
        items.push(`Your reimbursement request does not conform to the ${declineEmailAnchor(policyLink, 'Donor Shield Reimbursement Policy')}. Please review the policy and resubmit with corrections.`);
    }
    if (routing) {
        items.push(bankingTerms.routingEmailSentence(hereLink));
    }
    if (account) {
        items.push(bankingTerms.accountEmailSentence(hereLink));
    }
    if (items.length === 0) {
        return '';
    }
    const listItems = items.map((html) => `<li>${html}</li>`).join('');
    return `Dear ${name}, <br/><br/>We cannot process your claim as submitted. Please address the following:<br/><ul>${listItems}</ul>`;
}

function buildUnifiedLastParagraph(dashboardLink: string, policyLink: string): string {
    return `To update your request, ${declineEmailAnchor(dashboardLink, 'click here')}. <br/><br/> To review the Donor Shield Reimbursement Policy, ${declineEmailAnchor(policyLink, 'click here')}.<br/><br/>To view the Donor Shield guide, ${declineEmailAnchor('https://www.donorshield.com/kidneyguide/', 'click here')}<br/><br/>Once the requested information is submitted, your reimbursement request will be reviewed for eligibility and processing.`;
}

/** Lost-wage verification / Request Donor Review — matches Expense-style donor email (no banking chooser). */
function buildLostWageVerificationFirstParagraph(data: IWatrRequest, policyLink: string): string {
    return `Dear ${data.firstName}, <br/><br/>We are unable to approve your reimbursement request at this time because additional information is needed.<br/><br/> Please review the following item(s) and resubmit your request:`;
}

function buildLostWageVerificationLastParagraph(dashboardLink: string): string {
    return `To update your request, ${declineEmailAnchor(dashboardLink, 'click here')}.`;
}

const Decline = (props: IProps) => {
    const isFixable = props.declineType === 'Expense' ? props.hasLineItemsSelected === true : true;
    const [emailSubject, setEmailSubject] = useState('');
    const [emailContent, setEmailContent] = useState('');
    const [firstParagraph, setFirstParagraph] = useState('');
    const [lastParagraph, setLastParagraph] = useState('');
    const [showError, setShowError] = useState(false);
    const [selectionError, setSelectionError] = useState(false);
    const [declineRouting, setDeclineRouting] = useState(false);
    const [declineAccount, setDeclineAccount] = useState(false);
    const [declineReasonList, setDeclineReasonList] = useState<IClaimDeclineResponseItem[]>([])
    const [chosenDeclineIds, setChosenDeclineIds] = useState<number[]>([]);

    const previousShow = usePrevious(props.show);

    const bankingOnlyRepair = props.allowWageSelection === false;
    /** Lost-wage verification (e.g. Request Donor Review): wages implied; no banking chooser in modal. */
    const effectiveWages = !bankingOnlyRepair;
    const effectiveRouting = bankingOnlyRepair ? declineRouting : false;
    const effectiveAccount = bankingOnlyRepair ? declineAccount : false;
    const hasAnyIssueSelected = effectiveWages || effectiveRouting || effectiveAccount;

    const getPolicyLink = () => {
        if (!props.data) return '';
        return props.data.donationOrganType == DashDonationType.Liver ? 'https://www.donorshield.com/liverpolicy' : 'https://www.donorshield.com/policy';
    };

    useEffect(() => {
        if (previousShow == false && props.show == true) {
            setDeclineReasonList(getDeclineActions());
            if (props.data != null) {
                setDeclineRouting(false);
                setDeclineAccount(false);
                setChosenDeclineIds([]);
                setSelectionError(false);
                const newEmailSubject = `Donor Shield ${props.declineType} Reimbursement - Request Declined`;
                const policyLink = getPolicyLink();
                const dashboardLink = props.data.dashboardLink || '#';
                setFirstParagraph(
                    props.declineType === 'Expense'
                        ? isFixable
                            ? `Dear ${props.data.firstName}, <br/><br/>We are unable to approve your reimbursement request at this time because additional information is needed.<br/><br/> Please review the following item(s) and resubmit your request:`
                            : `Dear ${props.data.firstName}, <br/><br/>Your application has been declined.`
                        : props.allowWageSelection === false
                            ? ''
                            : buildLostWageVerificationFirstParagraph(props.data, policyLink)
                );
                setLastParagraph(
                    props.declineType === 'Expense'
                        ? isFixable
                            ? `To update your request, ${declineEmailAnchor(dashboardLink, 'click here')}. <br/><br/> To review the Donor Shield Reimbursement Policy, ${declineEmailAnchor(policyLink, 'click here')}.<br/><br/>To view the Donor Shield guide, ${declineEmailAnchor('https://www.donorshield.com/kidneyguide/', 'click here')}<br/><br/>Once the requested information is submitted, your reimbursement request will be reviewed for eligibility and processing.`
                            : ''
                        : buildUnifiedLastParagraph(dashboardLink, policyLink)
                );
                setEmailContent('');
                setEmailSubject(newEmailSubject);
                setShowError(false);
            }
        }
    }, [props.show, props.data, props.declineType, props.allowWageSelection, isFixable]);

    useEffect(() => {
        if (!props.show || props.data == null || props.declineType === 'Expense') {
            return;
        }
        const policyLink = getPolicyLink();
        const dashboardLink = props.data.dashboardLink || '#';
        if (props.allowWageSelection !== false) {
            setFirstParagraph(buildLostWageVerificationFirstParagraph(props.data, policyLink));
            setLastParagraph(buildUnifiedLastParagraph(dashboardLink, policyLink));
            return;
        }
        const routingForCopy = declineRouting;
        const accountForCopy = declineAccount;
        setFirstParagraph(buildUnifiedFirstParagraph(props.data, false, routingForCopy, accountForCopy, policyLink, dashboardLink));
        setLastParagraph(buildUnifiedLastParagraph(dashboardLink, policyLink));
        if (selectionError && (routingForCopy || accountForCopy)) {
            setSelectionError(false);
        }
    }, [declineRouting, declineAccount, props.show, props.data, props.declineType, props.allowWageSelection]);

    const handleInputChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let name = e.currentTarget.name;
        let value = e.currentTarget.value;

        if (name == 'emailSubject') {
            setEmailSubject(value);
        }

        if (name == 'emailContent') {
            setEmailContent(value);
            if (showError && value.trim().length > 0) {
                setShowError(false);
            }
        }
    };

    const hasMessageContent = chosenDeclineIds.length > 0 || (!!emailContent && emailContent.trim().length > 0);
    const emailContentRequired =
        (props.declineType === 'Expense' || (props.declineType !== 'Expense' && effectiveWages)) &&
        !hasMessageContent;

    const handleSendDecline = () => {
        if (props.declineType !== 'Expense' && !hasAnyIssueSelected) {
            setSelectionError(true);
            return;
        }

        if (emailContentRequired) {
            setShowError(true);
            alert('Please select at least one item to be reviewed or enter email content before sending the repair request.');
            return;
        }

        if (!emailSubject || emailSubject.trim().length === 0) {
            alert('Email Subject is required. Please provide a subject before sending the repair request.');
            return;
        }

        setShowError(false);
        setSelectionError(false);
        const contentForApi = (emailContent || '').replace(/\n/g, '|');
        const declineReasonSummary = declineReasonList
            .filter(x => chosenDeclineIds.includes(x.id))
            .map(x => (x.errorType || '').trim())
            .filter(x => x.length > 0)
            .join('; ');
        const selection = props.declineType === 'Expense'
            ? buildDeclineSelectionPayload(false, false, false, declineReasonSummary)
            : buildDeclineSelectionPayload(effectiveWages, effectiveRouting, effectiveAccount, declineReasonSummary);

        let beginning = firstParagraph;

        if (chosenDeclineIds.length > 0 && isFixable) {
            beginning += actionItems()
        }

        props.onDeclineRequest(props.data.id, emailSubject, beginning, contentForApi, lastParagraph, isFixable, true, selection);
    };

    const getLink = () => getPolicyLink();

    const getDeclineActions = () => {
        if (props.claimDeclineResponseList == null || props.claimDeclineResponseList.length == 0) {
            return []
        } else {
            return props.claimDeclineResponseList
                    .filter(x => 
                        (x.claimType == ClaimType.LWR && props.declineType != 'Expense')
                        || (x.claimType == ClaimType.DER && props.declineType == 'Expense')
                    )
        }
    }

    if (props.data == null)
        return null;

    const bankingTerms = getBankingReviewTerminology(props.data.country, props.data.isUsBank);

    const sendDisabled =
        !emailSubject ||
        emailSubject.trim().length === 0 ||
        emailContentRequired ||
        (props.declineType !== 'Expense' && !hasAnyIssueSelected);

    const handleDeclineReasonChoice = (e: React.FormEvent<HTMLInputElement>) => {
        let value = parseInt(e.currentTarget.value)
        let currentList = chosenDeclineIds.filter(x => x == (value));

        if (currentList.length > 0) {
            let newList = chosenDeclineIds.filter(x => x != value);
            setChosenDeclineIds(prevState => newList);
        } else {
            currentList = chosenDeclineIds;
            let newList = [...currentList, value]
            setChosenDeclineIds(prevState => newList);
        }
    }

    const actionItems = () => {
        let items = declineReasonList.filter(x => chosenDeclineIds.includes(x.id));

        if (items.length === 0) {
            return '';
        }

        let listItems = items.map(x => `<li>${x.automatedMessage}</li>`).join('');
        return `<ol>${listItems}</ol>`;
    }

    return (
        <Modal backdrop='static' bsSize='lg' show={props.show} onHide={props.onClose} className="modal-vertical-center" dialogClassName="decline-modal-dialog" style={{ display: 'flex', alignItems: 'center' }}>
            <Modal.Header closeButton>
                <Modal.Title>Confirm Decline for {props.data.firstName + ' ' + props.data.lastName}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {props.declineType !== 'Expense' && bankingOnlyRepair && (
                    <Well bsSize='sm' style={{ marginBottom: 16 }}>
                        <FormGroup>
                            <ControlLabel>
                                Select items to be reviewed
                                <span style={{ color: 'red' }}> *</span>
                            </ControlLabel>
                            <Checkbox
                                checked={declineRouting}
                                onChange={(e: FormEvent<Checkbox>) => setDeclineRouting((e.target as HTMLInputElement).checked)}
                            >
                                {bankingTerms.routingLabel}
                            </Checkbox>
                            <Checkbox
                                checked={declineAccount}
                                onChange={(e: FormEvent<Checkbox>) => setDeclineAccount((e.target as HTMLInputElement).checked)}
                            >
                                {bankingTerms.accountLabel}
                            </Checkbox>
                            {selectionError && (
                                <Alert bsStyle="danger" style={{ marginTop: 8, marginBottom: 0, padding: '8px' }}>
                                    {bankingTerms.selectionError}
                                </Alert>
                            )}
                        </FormGroup>
                    </Well>
                )}

                {isFixable && (
                    <Well>
                        <ControlLabel>
                            Select items to be reviewed
                        </ControlLabel>
                        <div className='decline-reason-grid'>
                            {declineReasonList.map((x, index) => (
                                <div key={'type-' + index}>
                                    <input
                                        style={{ marginRight: '5px' }}
                                        type="checkbox"
                                        name={x.errorType}
                                        value={x.id}
                                        checked={chosenDeclineIds.includes(x.id)}
                                        onChange={handleDeclineReasonChoice}
                                    />
                                    <span style={{ margin: '4px' }}>
                                        {x.errorType}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </Well>
                )}

                <>
                    <FormGroup>
                        <ControlLabel>Email Subject</ControlLabel>
                        <FormControl
                            name='emailSubject'
                            id='EmailSubject'
                            value={emailSubject}
                            onChange={handleInputChange} />
                    </FormGroup>

                    <Well bsSize='sm'>
                        <FormGroup validationState={showError ? 'error' : null}>
                            <ControlLabel>Email Content {emailContentRequired && <span style={{ color: 'red' }}>*</span>}</ControlLabel>
                            {showError && (
                                <Alert bsStyle="danger" style={{ marginTop: '5px', marginBottom: '10px', padding: '8px' }}>
                                    Please select at least one item to be reviewed or enter email content before sending.
                                </Alert>
                            )}
                            <div style={{ marginBottom: 5 }} className='form-control-static'>
                                {props.declineType !== 'Expense' && !hasAnyIssueSelected ? (
                                    <em>Select items to be reviewed above to generate the donor message.</em>
                                ) : (
                                    <>
                                        {ReactHtmlParser(firstParagraph)}
                                        {chosenDeclineIds.length > 0 && isFixable && ReactHtmlParser(actionItems())}
                                    </>
                                )}
                            </div>

                            <FormControl
                                componentClass='textarea'
                                placeholder='Type your message here...'
                                name='emailContent'
                                value={emailContent}
                                style={{ height: '200px' }}
                                onChange={handleInputChange}
                                required={emailContentRequired}
                            />

                            <div style={{ marginTop: 5 }} className='form-control-static'>{ReactHtmlParser(lastParagraph)}</div>
                        </FormGroup>
                    </Well>
                </>
            </Modal.Body>
            <Modal.Footer>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', width: '100%' }}>
                    <Button onClick={props.onClose}>Cancel</Button>
                    {isFixable ? (
                        <Button
                            bsStyle='primary'
                            onClick={handleSendDecline}
                            disabled={sendDisabled}
                        >
                            Send Repair Request
                        </Button>
                    ) : (
                        <Button bsStyle='danger' onClick={handleSendDecline} disabled={sendDisabled}>Send Decline</Button>
                    )}
                </div>
            </Modal.Footer>
        </Modal>
    );
}

export default Decline;
